import { useState, useEffect } from 'react';
import { ShieldCheck, Loader as Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getRememberDeviceFlag, setRememberDeviceFlag } from '@/lib/trustedDevices';

interface MfaChallengeProps {
  onVerified: () => void;
}

export default function MfaChallenge({ onVerified }: MfaChallengeProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [factorId, setFactorId] = useState('');
  const [ready, setReady] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(getRememberDeviceFlag);

  useEffect(() => {
    async function loadFactor() {
      const { data, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) {
        setError(listError.message);
        setReady(true);
        return;
      }

      const totpFactor = data.totp.find((f) => f.status === 'verified');
      if (totpFactor) {
        setFactorId(totpFactor.id);
      } else {
        setError('No verified TOTP factor found.');
      }
      setReady(true);
    }

    loadFactor();
  }, []);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!factorId) return;

    setLoading(true);
    setError('');

    const { data: challengeData, error: challengeError } =
      await supabase.auth.mfa.challenge({ factorId });

    if (challengeError) {
      setError(challengeError.message);
      setLoading(false);
      return;
    }

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });

    if (verifyError) {
      setError(verifyError.message);
      setCode('');
      setLoading(false);
      return;
    }

    setRememberDeviceFlag(rememberDevice);
    onVerified();
  }

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <div className="w-full max-w-md bg-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-teal-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Two-Factor Authentication</h2>
            <p className="text-xs text-white/60">Enter the code from your authenticator app</p>
          </div>
        </div>

        {error && (
          <div className="mb-5 p-3 bg-red-500/20 border border-red-400/30 rounded-xl">
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label htmlFor="mfa-code" className="block text-sm font-medium text-white mb-1.5">
              Verification code
            </label>
            <input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              required
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-sm text-white text-center tracking-[0.5em] font-mono focus:ring-2 focus:ring-teal-400/30 focus:border-teal-400/50 outline-none transition-all duration-200 placeholder:text-white/40 placeholder:tracking-[0.5em]"
            />
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              className="w-4 h-4 rounded border-slate-600 bg-white/10 text-teal-500 focus:ring-teal-400/30 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-xs text-white/60">Remember this device for 30 days</span>
          </label>

          <button
            type="submit"
            disabled={loading || code.length < 6 || !factorId}
            className="w-full bg-teal-500/30 glass-hover-teal backdrop-blur-xl border border-teal-400/30 text-white font-semibold py-2.5 rounded-xl disabled:bg-slate-500/30 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm shadow-lg shadow-teal-500/15"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <ShieldCheck size={16} />
                Verify
              </>
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-white/50">
          Open your authenticator app and enter the 6-digit code.
        </p>
      </div>
    </div>
  );
}
